<template>
  <el-container class="admin-page">
    <el-header class="admin-page__title">
      用户登录信息
    </el-header>

    <el-main>
      <div class="admin-page__toolbar">
        <div class="admin-page__toolbar-start">
          <el-input
            v-model="searchQuery"
            placeholder="输入授权码、用户名或设备ID进行搜索"
            clearable
            @input="handleSearch"
            :style="{ width: isMobile ? '100%' : '300px' }"
          >
            <template #prefix>
              <el-icon><Search /></el-icon>
            </template>
          </el-input>

          <el-date-picker
            v-model="dateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            @change="handleDateChange"
            clearable
            :style="{ width: isMobile ? '100%' : '280px' }"
            :teleported="!isMobile"
            :popper-class="isMobile ? 'mobile-date-picker-dropdown' : ''"
          />

          <el-button type="primary" @click="fetchLoginData" :style="{ width: isMobile ? '100%' : 'auto' }">
            查询
          </el-button>
        </div>
      </div>

      <!-- PC端表格视图 -->
      <el-table 
        v-if="!isMobile"
        :data="loginData" 
        style="width: 100%"
        stripe
        v-loading="loading"
      >
        <el-table-column prop="auth_code" label="授权码" width="200" />
        <el-table-column prop="user_name" label="用户名" width="120" />
        <el-table-column prop="device_id" label="设备ID" min-width="180" show-overflow-tooltip />
        <el-table-column prop="operation_time" label="操作时间" width="170" sortable />
        <el-table-column prop="event_type" label="事件类型" width="100" />
      </el-table>

      <!-- 移动端卡片视图 -->
      <div v-else class="mobile-cards" v-loading="loading">
        <el-card v-for="(item, index) in loginData" :key="index" class="mobile-card">
          <div class="card-item">
            <span class="label">用户名：</span>
            <span class="value">{{ item.user_name }}</span>
          </div>
          <div class="card-item">
            <span class="label">授权码：</span>
            <span class="value">{{ item.auth_code }}</span>
          </div>
          <div class="card-item">
            <span class="label">设备ID：</span>
            <span class="value">{{ item.device_id }}</span>
          </div>
          <div class="card-item">
            <span class="label">操作时间：</span>
            <span class="value">{{ item.operation_time }}</span>
          </div>
          <div class="card-item">
            <span class="label">事件类型：</span>
            <el-tag :type="item.event_type === '登录' ? 'success' : 'info'" size="small">
              {{ item.event_type }}
            </el-tag>
          </div>
        </el-card>
      </div>

      <!-- 分页器 -->
      <el-pagination
        class="admin-page__pager"
        background
        :layout="isMobile ? 'prev, pager, next' : 'total, sizes, prev, pager, next, jumper'"
        :total="totalCount"
        :page-size="pageSize"
        :current-page="currentPage"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </el-main>
  </el-container>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { ElMessage } from 'element-plus';
import authApi from '@/js/connect';

const loginData = ref([]);
const searchQuery = ref('');
const dateRange = ref([]);
const loading = ref(false);
const totalCount = ref(0);
const pageSize = ref(50);
const currentPage = ref(1);

const maskAuthCode = (code) => {
  if (!code || code.length <= 8) return code;
  return code.slice(0, 4) + '********' + code.slice(-4);
};

const fetchLoginData = async () => {
  loading.value = true;
  try {
    const params = {
      page: currentPage.value,
      per_page: pageSize.value,
      start_date: dateRange.value && dateRange.value[0] ? formatDate(dateRange.value[0]) : undefined,
      end_date: dateRange.value && dateRange.value[1] ? formatDate(dateRange.value[1]) : undefined,
      search: searchQuery.value,
      sort: 'operation_time',
      order: 'desc'
    };
    console.log("start_date, end_date", params.start_date, params.end_date);
    const response = await authApi.getAllLoginLogs(params);
    console.log('Backend response:', response);
    console.log('Response data:', response.data);

    if (response.data && Array.isArray(response.data.logs)) {
      loginData.value = response.data.logs.map(log => ({
        ...log,
        user_name: log.user_name || '未知用户',
        auth_code: maskAuthCode(log.auth_code || '未知授权码'),
        operation_time: log.operation_time ? new Date(log.operation_time).toLocaleString() : '-',
        event_type: log.event_type === 'login' ? '登录' : '登出'
      }));
      totalCount.value = response.data.total_count;
    } else {
      console.error('Unexpected response format:', response.data);
      ElMessage.error('获取登录日志失败：数据格式不正确');
    }
  } catch (error) {
    console.error('获取登录日志失败:', error);
    ElMessage.error('获取登录日志失败，请稍后重试');
  } finally {
    loading.value = false;
  }
};

// 新增的日期格式化函数
const formatDate = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const handleSizeChange = (val) => {
  pageSize.value = val;
  fetchLoginData();
};

const handleCurrentChange = (val) => {
  currentPage.value = val;
  fetchLoginData();
};

const handleDateChange = () => {
  currentPage.value = 1; // 重置页码
  fetchLoginData();
};

const handleSearch = () => {
  currentPage.value = 1; // 重置页码
  fetchLoginData();
};

// 添加移动设备检测
const isMobile = computed(() => {
  return window.innerWidth <= 768;
});

// 监听窗口大小变化
onMounted(() => {
  fetchLoginData();
  window.addEventListener('resize', () => {
    isMobile.value = window.innerWidth <= 768;
  });
});
</script>

<style scoped>
.mobile-cards {
  display: flex;
  flex-direction: column;
  gap: 10px;
  position: relative;
  min-height: 200px;
}

.mobile-card {
  margin-bottom: 10px;
  border-radius: var(--dca-radius);
  background-color: var(--dca-surface);
  border: 1px solid var(--dca-border);
  box-shadow: none;
  transition: transform 0.3s ease;
}

.mobile-card:active {
  transform: scale(0.98);
}

.card-item {
  display: flex;
  padding: 8px 0;
  border-bottom: 1px solid var(--dca-border);
}

.card-item:last-child {
  border-bottom: none;
}

.card-item .label {
  width: 80px;
  color: var(--dca-text-muted);
  font-size: 14px;
}

.card-item .value {
  flex: 1;
  color: var(--dca-text);
  font-size: 14px;
  word-break: break-all;
}

:deep(.mobile-date-picker-dropdown) {
  position: fixed !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  margin: 0 !important;
  width: 90vw !important;
  max-width: 400px;
  max-height: 90vh;
  overflow-y: auto;
  border-radius: var(--dca-radius);
  background-color: var(--dca-surface);
  border-color: var(--dca-border);
  box-shadow: var(--dca-shadow);
  z-index: 2001 !important;
}

:deep(.mobile-date-picker-dropdown)::before {
  content: '';
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: -1;
}

:deep(.mobile-date-picker-dropdown) .el-date-range-picker__content {
  padding: 10px;
}

:deep(.mobile-date-picker-dropdown) .el-date-range-picker__header {
  margin: 8px;
}

:deep(.mobile-date-picker-dropdown) .el-date-table th,
:deep(.mobile-date-picker-dropdown) .el-date-table td {
  padding: 4px;
}
</style>
